/**
 * LotteryManagement — مدیریت قرعه‌کشی‌های مایلستونی
 *
 * لیست کمپین‌های قرعه‌کشی (سراسری + فروشگاهی) با فیلتر، ساخت/ویرایش کمپین سراسری،
 * تایید/رد کمپین‌های فروشگاهی، توقف/ادامه/لغو، و مودال جزئیات (شرکت‌کنندگان، مدارک،
 * قرعه‌کشی commit-reveal).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import { useSnackbar } from 'notistack';
import {
  PageHeader,
  FilterPanel,
  ResponsiveTable,
  ConfirmDialog,
  ActionMenu,
  Column,
  ActiveFilter,
  ActionMenuItem,
} from '../components/common';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import LotteryCampaignDetailModal, { CampaignStatusChip } from '../components/lottery/LotteryCampaignDetailModal';
import CampaignWizard from '../components/lottery/CampaignWizard';
import { formatDate } from '../utils/dateUtils';
import lotteryService, {
  CAMPAIGN_STATUS_LABELS,
  ENTRY_MODE_LABELS,
  LotteryCampaignItem,
  LotteryEntryMode,
  SCOPE_LABELS,
  userDisplayName,
} from '../services/lottery.service';

type RowAction = 'approve' | 'reject' | 'pause' | 'resume' | 'cancel';

const ACTION_META: Record<RowAction, { title: string; message: string; confirmText: string; color: 'success' | 'error' | 'warning' | 'primary' }> = {
  approve: {
    title: 'تایید کمپین',
    message: 'کمپین تایید و فعال می‌شود. ادامه می‌دهید؟',
    confirmText: 'تایید کمپین',
    color: 'success',
  },
  reject: {
    title: 'رد کمپین',
    message: 'کمپین رد می‌شود و سازنده دلیل آن را می‌بیند. لطفا دلیل رد را بنویسید:',
    confirmText: 'رد کمپین',
    color: 'error',
  },
  pause: {
    title: 'توقف کمپین',
    message: 'کمپین موقتا متوقف می‌شود و کاربران پیشرفت جدیدی ثبت نمی‌کنند. ادامه می‌دهید؟',
    confirmText: 'توقف',
    color: 'warning',
  },
  resume: {
    title: 'ادامه کمپین',
    message: 'کمپین دوباره فعال می‌شود. ادامه می‌دهید؟',
    confirmText: 'ادامه کمپین',
    color: 'success',
  },
  cancel: {
    title: 'لغو کمپین',
    message: 'کمپین به‌صورت دائمی لغو می‌شود و قابل بازگشت نیست. ادامه می‌دهید؟',
    confirmText: 'لغو کمپین',
    color: 'error',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LotteryManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List state
  const [items, setItems] = useState<LotteryCampaignItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('');

  // Row action confirm dialog
  const [pendingAction, setPendingAction] = useState<{ type: RowAction; row: LotteryCampaignItem } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Detail modal
  const [detailId, setDetailId] = useState<string | null>(null);

  // Create/Edit wizard
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LotteryCampaignItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lotteryService.getCampaigns({
        page,
        limit,
        search: search.trim() || undefined,
        status: status || undefined,
        scope: scope || undefined,
      });
      setItems(res.items);
      setTotal(res.meta.totalItems);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت لیست کمپین‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, scope, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setStatus('');
    setScope('');
    setPage(1);
  };

  const activeFilters: ActiveFilter[] = [
    ...(search
      ? [{ key: 'search', label: `جستجو: ${search}`, onRemove: () => { setSearch(''); setSearchInput(''); setPage(1); } }]
      : []),
    ...(status
      ? [{ key: 'status', label: `وضعیت: ${CAMPAIGN_STATUS_LABELS[status as keyof typeof CAMPAIGN_STATUS_LABELS] || status}`, onRemove: () => { setStatus(''); setPage(1); } }]
      : []),
    ...(scope
      ? [{ key: 'scope', label: `محدوده: ${SCOPE_LABELS[scope as keyof typeof SCOPE_LABELS] || scope}`, onRemove: () => { setScope(''); setPage(1); } }]
      : []),
  ];

  // ------------------------------------------------------------ row actions

  const runRowAction = async () => {
    if (!pendingAction) return;
    const { type, row } = pendingAction;
    if (type === 'reject' && !rejectReason.trim()) {
      enqueueSnackbar('نوشتن دلیل رد الزامی است', { variant: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      if (type === 'approve') await lotteryService.approve(row._id);
      else if (type === 'reject') await lotteryService.reject(row._id, rejectReason.trim());
      else if (type === 'pause') await lotteryService.pause(row._id);
      else if (type === 'resume') await lotteryService.resume(row._id);
      else await lotteryService.cancel(row._id);
      const successMsg: Record<RowAction, string> = {
        approve: 'کمپین تایید و فعال شد',
        reject: 'کمپین رد شد',
        pause: 'کمپین متوقف شد',
        resume: 'کمپین دوباره فعال شد',
        cancel: 'کمپین لغو شد',
      };
      enqueueSnackbar(successMsg[type], { variant: 'success' });
      setPendingAction(null);
      setRejectReason('');
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در انجام عملیات', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // ------------------------------------------------------------- form logic
  // فرم ساخت/ویرایش به ویزارد components/lottery/CampaignWizard منتقل شد

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: LotteryCampaignItem) => {
    setEditing(row);
    setFormOpen(true);
  };

  // ---------------------------------------------------------------- columns

  const columns: Column<LotteryCampaignItem>[] = [
    {
      id: 'title',
      label: 'عنوان',
      priority: 1,
      format: (_v, row) => {
        const store: any = row.storeId;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {SCOPE_LABELS[row.scope] || row.scope}
              {store?.name ? ` — ${store.name}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 2,
      format: (v) => <CampaignStatusChip status={v} />,
    },
    {
      id: 'startAt',
      label: 'بازه برگزاری',
      hideOnMobile: true,
      format: (_v, row) => (
        <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
          {formatDate(row.startAt)}
          <br />
          تا {formatDate(row.endAt)}
        </Typography>
      ),
    },
    {
      id: 'entryMode',
      label: 'شیوه بلیت‌دهی',
      detail: true,
      format: (v) => ENTRY_MODE_LABELS[v as LotteryEntryMode] || v,
    },
    {
      id: 'stats.participants',
      label: 'شرکت‌کننده',
      priority: 3,
      align: 'center',
      format: (v) => (
        <Chip size="small" variant="outlined" color="primary" label={toPersianDigits(String(v || 0))} />
      ),
    },
    {
      id: 'createdBy',
      label: 'سازنده',
      detail: true,
      format: (v) => userDisplayName(v),
    },
    {
      id: 'createdAt',
      label: 'تاریخ ساخت',
      hideOnMobile: true,
      format: (v) => (v ? formatDate(v) : '-'),
    },
  ];

  const rowMenuItems = (row: LotteryCampaignItem): ActionMenuItem[] => [
    {
      label: 'ویرایش',
      icon: <EditIcon fontSize="small" />,
      onClick: () => openEdit(row),
      hidden: !(row.status === 'draft' || row.status === 'rejected'),
    },
    {
      label: row.status === 'draft' ? 'تایید و فعال‌سازی' : 'تایید کمپین',
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      onClick: () => setPendingAction({ type: 'approve', row }),
      // بک‌اند تایید پیش‌نویس را هم مجاز می‌داند — ادمین می‌تواند مستقیم فعال کند
      hidden: !['pending_approval', 'draft'].includes(row.status),
    },
    {
      label: 'رد کمپین',
      icon: <CancelIcon fontSize="small" />,
      color: 'error',
      onClick: () => {
        setRejectReason('');
        setPendingAction({ type: 'reject', row });
      },
      hidden: row.status !== 'pending_approval',
    },
    {
      label: 'توقف',
      icon: <PauseCircleOutlineIcon fontSize="small" />,
      color: 'warning',
      onClick: () => setPendingAction({ type: 'pause', row }),
      hidden: row.status !== 'active',
    },
    {
      label: 'ادامه کمپین',
      icon: <PlayCircleOutlineIcon fontSize="small" />,
      color: 'success',
      onClick: () => setPendingAction({ type: 'resume', row }),
      hidden: row.status !== 'paused',
    },
    {
      label: 'لغو کمپین',
      icon: <BlockIcon fontSize="small" />,
      color: 'error',
      divider: true,
      onClick: () => setPendingAction({ type: 'cancel', row }),
      hidden: !['draft', 'pending_approval', 'active', 'paused'].includes(row.status),
    },
  ];

  // ----------------------------------------------------------------- render

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="مدیریت قرعه‌کشی‌ها"
        subtitle="کمپین‌های قرعه‌کشی مایلستونی — ساخت، تایید، اجرای قرعه‌کشی اثبات‌پذیر و تحویل جوایز"
        breadcrumbs={[{ label: 'قرعه‌کشی‌ها' }]}
        actions={[
          { label: 'بروزرسانی', onClick: () => load(), icon: <RefreshIcon />, variant: 'outlined', color: 'primary' },
          { label: 'کمپین جدید', onClick: openCreate, icon: <AddIcon /> },
        ]}
      />

      <FilterPanel
        searchSlot={
          <TextField
            fullWidth
            size="small"
            placeholder="جستجو در عنوان کمپین..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            InputProps={{ endAdornment: <SearchIcon fontSize="small" color="action" /> }}
          />
        }
        activeFilters={activeFilters}
        onApply={applySearch}
        onClear={clearFilters}
      >
        <TextField
          select
          size="small"
          label="وضعیت"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">همه وضعیت‌ها</MenuItem>
          {(Object.keys(CAMPAIGN_STATUS_LABELS) as (keyof typeof CAMPAIGN_STATUS_LABELS)[]).map((s) => (
            <MenuItem key={s} value={s}>
              {CAMPAIGN_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="محدوده"
          value={scope}
          onChange={(e) => {
            setScope(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">همه</MenuItem>
          <MenuItem value="site">{SCOPE_LABELS.site}</MenuItem>
          <MenuItem value="store">{SCOPE_LABELS.store}</MenuItem>
        </TextField>
      </FilterPanel>

      <ResponsiveTable<LotteryCampaignItem>
        columns={columns}
        data={items}
        loading={loading}
        keyField="_id"
        emptyMessage="کمپین قرعه‌کشی یافت نشد"
        pagination
        page={page - 1}
        rowsPerPage={limit}
        totalCount={total}
        onPageChange={(p) => setPage(p + 1)}
        onRowsPerPageChange={(rpp) => {
          setLimit(rpp);
          setPage(1);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        onRowClick={(row) => setDetailId(row._id)}
        renderActions={(row) => (
          <>
            <Tooltip title="جزئیات کامل" arrow>
              <IconButton size="small" onClick={() => setDetailId(row._id)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <ActionMenu items={rowMenuItems(row)} />
          </>
        )}
      />

      {/* مودال جزئیات: آمار + شرکت‌کنندگان + مدارک + قرعه‌کشی */}
      <LotteryCampaignDetailModal
        campaignId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onChanged={load}
      />

      {/* دیالوگ تایید عملیات ردیف */}
      <ConfirmDialog
        open={!!pendingAction}
        title={pendingAction ? ACTION_META[pendingAction.type].title : ''}
        message={
          pendingAction
            ? `کمپین «${pendingAction.row.title}» — ${ACTION_META[pendingAction.type].message}`
            : ''
        }
        confirmText={pendingAction ? ACTION_META[pendingAction.type].confirmText : 'تایید'}
        confirmColor={pendingAction ? ACTION_META[pendingAction.type].color : 'primary'}
        loading={actionLoading}
        onCancel={() => setPendingAction(null)}
        onConfirm={runRowAction}
      >
        {pendingAction?.type === 'reject' && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            autoFocus
            placeholder="دلیل رد (الزامی)..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        )}
      </ConfirmDialog>

      {/* ویزارد ۴ مرحله‌ای ساخت/ویرایش کمپین سراسری */}
      <CampaignWizard
        open={formOpen}
        campaign={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
    </Box>
  );
};

export default LotteryManagement;
